<template>
  <div v-if="device.isMobile">
    <!-- Mobile Filter Bar -->
    <div class="mobile-filter-bar px-2 py-2">
      <div class="d-flex align-center">
        <!-- Filter button -->
        <v-btn
          icon
          small
          color="primary"
          @click="filterDialog = true"
          class="mr-2 flex-shrink-0"
        >
          <v-icon>mdi-tune</v-icon>
        </v-btn>
        <!-- Scrollable chips -->
        <div class="filter-chips-scroll d-flex align-center flex-grow-1">
          <template v-for="(filter, key) in activeFilters">
            <v-chip
              v-if="filter.visible"
              :key="key"
              small
              class="mr-2 flex-shrink-0"
              :close="filter.clearable"
              @click="openSingleFilter(key)"
              @click:close="clearFilter(key)"
            >
              <v-icon left small v-if="filter.icon">{{ filter.icon }}</v-icon>
              {{ filter.chipText }}
            </v-chip>
          </template>
        </div>
        <!-- Actions slot (right side) -->
        <div class="filter-actions flex-shrink-0 d-flex align-center">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>

    <!-- Single Filter Bottom Sheet -->
    <v-bottom-sheet v-model="singleFilterDialog" inset>
      <v-card rounded="0">
        <v-card-title class="d-flex align-center py-3">
          <span>{{ currentFilterConfig?.label || $t("filters") }}</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="singleFilterDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-4">
          <slot :name="'filter-' + activeFilter" :filter="currentFilterConfig">
            <!-- Default filter rendering based on type -->
            <template v-if="currentFilterConfig">
              <!-- Date picker -->
              <date-picker
                v-if="currentFilterConfig.type === 'date'"
                :value="currentFilterConfig.value"
                @input="updateFilterValue(activeFilter, $event)"
                :single-date="currentFilterConfig.singleDate"
                outlined
              />

              <!-- Autocomplete (single or multiple) -->
              <autocomplete
                v-else-if="currentFilterConfig.type === 'autocomplete'"
                :label="currentFilterConfig.label"
                :items="currentFilterConfig.items"
                :value="currentFilterConfig.value"
                @input="updateFilterValue(activeFilter, $event)"
                :multiple="currentFilterConfig.multiple"
                :return-object="currentFilterConfig.returnObject"
                :item-text="currentFilterConfig.itemText || 'text'"
                :item-value="currentFilterConfig.itemValue || 'value'"
                :clearable="currentFilterConfig.clearable !== false"
                :chips="currentFilterConfig.multiple"
                :small-chips="currentFilterConfig.multiple"
                :deletable-chips="currentFilterConfig.multiple"
                hide-details
                outlined
              />

              <!-- Dropdown -->
              <dropdown
                v-else-if="currentFilterConfig.type === 'dropdown'"
                :label="currentFilterConfig.label"
                :items="currentFilterConfig.items"
                :value="currentFilterConfig.value"
                @input="updateFilterValue(activeFilter, $event)"
                :item-text="currentFilterConfig.itemText || 'text'"
                :item-value="currentFilterConfig.itemValue || 'value'"
                hide-details
                outlined
              />

              <!-- Text field / Search -->
              <text-field
                v-else-if="
                  currentFilterConfig.type === 'text' ||
                  currentFilterConfig.type === 'search'
                "
                :label="currentFilterConfig.label"
                :value="currentFilterConfig.value"
                @input="updateFilterValue(activeFilter, $event)"
                :append-icon="
                  currentFilterConfig.type === 'search' ? 'mdi-magnify' : undefined
                "
                hide-details
                outlined
              />
            </template>
          </slot>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn block color="primary" @click="applySingleFilter">
            <v-icon left>mdi-check</v-icon>
            {{ $t("apply_filters") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>

    <!-- Full Filter Bottom Sheet (all filters) -->
    <v-bottom-sheet v-model="filterDialog" scrollable>
      <v-card rounded="0">
        <v-card-title class="d-flex align-center py-3">
          <span>{{ $t("filters") }}</span>
          <v-spacer></v-spacer>
          <v-btn text small color="primary" @click="resetFilters">
            {{ $t("reset") }}
          </v-btn>
          <v-btn icon small @click="filterDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-0" style="max-height: 60vh; overflow-y: auto">
          <v-row>
            <template v-for="(filter, key) in filters">
              <v-col :key="key" cols="12" v-if="filter.showInFullDialog !== false">
                <slot :name="'filter-' + key" :filter="filter">
                  <!-- Date picker -->
                  <date-picker
                    v-if="filter.type === 'date'"
                    :value="filter.value"
                    @input="updateFilterValue(key, $event)"
                    :single-date="filter.singleDate"
                    outlined
                  />

                  <!-- Autocomplete (single or multiple) -->
                  <autocomplete
                    v-else-if="filter.type === 'autocomplete'"
                    :label="filter.label"
                    :items="filter.items"
                    :value="filter.value"
                    @input="updateFilterValue(key, $event)"
                    :multiple="filter.multiple"
                    :return-object="filter.returnObject"
                    :item-text="filter.itemText || 'text'"
                    :item-value="filter.itemValue || 'value'"
                    :clearable="filter.clearable !== false"
                    :chips="filter.multiple"
                    :small-chips="filter.multiple"
                    :deletable-chips="filter.multiple"
                    hide-details
                    outlined
                  />

                  <!-- Dropdown -->
                  <dropdown
                    v-else-if="filter.type === 'dropdown'"
                    :label="filter.label"
                    :items="filter.items"
                    :value="filter.value"
                    @input="updateFilterValue(key, $event)"
                    :item-text="filter.itemText || 'text'"
                    :item-value="filter.itemValue || 'value'"
                    hide-details
                    outlined
                  />

                  <!-- Text field / Search -->
                  <text-field
                    v-else-if="filter.type === 'text' || filter.type === 'search'"
                    :label="filter.label"
                    :value="filter.value"
                    @input="updateFilterValue(key, $event)"
                    :append-icon="filter.type === 'search' ? 'mdi-magnify' : undefined"
                    hide-details
                    outlined
                  />
                </slot>
              </v-col>
            </template>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn block color="primary" @click="applyFilters">
            <v-icon left>mdi-check</v-icon>
            {{ $t("apply_filters") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
/**
 * MobileFilters - Generic mobile filter component
 *
 * Usage:
 * <mobile-filters
 *   :filters="filterConfig"
 *   @update:filter="handleFilterUpdate"
 *   @apply="handleApply"
 *   @reset="handleReset"
 * />
 *
 * Filter config example:
 * {
 *   dateRange: {
 *     type: 'date',
 *     label: 'Date',
 *     icon: 'mdi-calendar',
 *     value: [],
 *     chipText: '01.12. - 08.12.',
 *     visible: true,
 *     clearable: true
 *   },
 *   type: {
 *     type: 'autocomplete',
 *     label: 'Type',
 *     icon: 'mdi-tag',
 *     items: [...],
 *     value: [],
 *     multiple: true,
 *     chipText: 'Promocija',
 *     visible: true,
 *     clearable: true
 *   },
 *   search: {
 *     type: 'search',
 *     label: 'Search',
 *     icon: 'mdi-magnify',
 *     value: '',
 *     chipText: 'search term',
 *     visible: true,
 *     clearable: true
 *   }
 * }
 */
export default {
  name: "MobileFilters",
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      filterDialog: false,
      singleFilterDialog: false,
      activeFilter: null,
    };
  },
  computed: {
    activeFilters() {
      // Return only filters that should show as chips
      const result = {};
      for (const [key, filter] of Object.entries(this.filters)) {
        if (filter.visible) {
          result[key] = filter;
        }
      }
      return result;
    },
    currentFilterConfig() {
      return this.activeFilter ? this.filters[this.activeFilter] : null;
    },
  },
  methods: {
    openSingleFilter(filterKey) {
      this.activeFilter = filterKey;
      this.singleFilterDialog = true;
    },
    updateFilterValue(key, value) {
      this.$emit("update:filter", { key, value });
    },
    clearFilter(key) {
      const filter = this.filters[key];
      let emptyValue = "";
      if (filter.type === "date" || filter.multiple) {
        emptyValue = [];
      }
      this.$emit("update:filter", { key, value: emptyValue });
      this.$emit("apply");
    },
    applySingleFilter() {
      this.singleFilterDialog = false;
      this.$emit("apply");
    },
    applyFilters() {
      this.filterDialog = false;
      this.$emit("apply");
    },
    resetFilters() {
      this.$emit("reset");
    },
  },
};
</script>

<style scoped>
.mobile-filter-bar {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.filter-chips-scroll {
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.filter-chips-scroll::-webkit-scrollbar {
  display: none;
}
</style>
